// Phase 19 Plan 02 Task 2 — ReplayScreen body (META-03 web).
//
// REPLACES the Plan 08-08 placeholder. The /replay/:matchId route from Plan
// 15-08 now renders a functional turn-by-turn text-list viewer.
//
// Per D-019-T2-C (Claude's Discretion, 19-CONTEXT.md): the new format
// renders directly — NO "Replay viewer — text format (full playback in v2)"
// affordance banner. The text-list IS the v1 product surface; framing it
// as a v2-preview undersells the feature.
//
// Per D-019-T2-D: the accordion expand-detail is an INLINE expansion (not a
// modal). Mirrors the Phase 15 Plan 15-09 TournamentCard inline-expansion
// decision. Single render tree, single component.
//
// Plan 19-01 (Wave 1, on main) ships GET /api/replay/{matchId} as inline
// gzipped JSON (D-019-T2-A). The browser's fetch transparently decompresses,
// so calling `apiFetch().json()` yields the parsed ReplayJson directly.
//
// Load-state machine (mirrors ProfileScreen.tsx):
//   loading → loaded { replay }
//           → error  { errorKind: 'unauthorized'|'forbidden'|'not_found'|'gone'|'generic' }
//
// Error envelope mapping (Plan 19-01 contract):
//   401 → 'unauthorized'  (apiFetch already cleared auth — copy invites
//                          re-sign-in via the back-to-lobby CTA)
//   403 → 'forbidden'     (non-participant)
//   404 → 'not_found'     (no such match)
//   410 → 'gone'          (in-progress or cleaned-up; D-019-T2-B unified)
//   *   → 'generic'       (5xx after apiFetch's single retry, or network)

import { type JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'
import { useDocumentTitle } from '../../i18n/useDocumentTitle.js'
import { ApiError } from '../../net/client'
import {
  fetchReplay,
  type PhaseABody,
  type PhaseBBody,
  type ReplayResponse,
  type ReplayTurnRow,
} from '../../net/replay'
import { useRouter } from '../../router/router'
import { Board } from '../components/Board'
import { Btn } from '../components/Btn'
import { Icon } from '../components/Icon'
import { Screen, ScreenHeader } from '../components/Screen.js'
import { Seg, type SegOption } from '../components/Seg'

interface ReplayScreenProps {
  readonly id: string
}

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly replay: ReplayResponse }
  | {
      readonly kind: 'error'
      readonly errorKind:
        | 'unauthorized'
        | 'forbidden'
        | 'not_found'
        | 'gone'
        | 'generic'
    }

const ERROR_COPY_KEY: Record<
  Exclude<LoadState, { kind: 'loading' } | { kind: 'loaded' }>['errorKind'],
  string
> = {
  unauthorized: 'replay.error.unauthorized',
  forbidden: 'replay.error.forbidden',
  not_found: 'replay.error.not_found',
  gone: 'replay.error.gone',
  generic: 'replay.error.generic',
}

const OUTCOME_KEY_FOR_STATUS: Record<string, string> = {
  completed: 'replay.outcome.completed',
  won: 'replay.outcome.completed',
  lost: 'replay.outcome.completed',
  drawn: 'replay.outcome.draw',
  abandoned: 'replay.outcome.abandoned',
  timeout_resolved: 'replay.outcome.timeout',
  stalemate_resolved: 'replay.outcome.stalemate',
}

function mapStatusToError(
  status: number,
): LoadState['kind'] extends 'error'
  ? never
  : 'unauthorized' | 'forbidden' | 'not_found' | 'gone' | 'generic' {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 410) return 'gone'
  return 'generic'
}

function phaseASummary(phaseA: PhaseABody, intl: IntlShape): string {
  if (phaseA.kind === 'word') {
    return intl.formatMessage(
      { id: 'replay.turn.phase_a.word' },
      { word: phaseA.word },
    )
  }
  // Plan 30-04 cleanup: the legacy `swap` Phase A branch is dead under
  // Runespring (Phase 24 RUNE-05 removed the per-player swap surface). The
  // PhaseABody union still carries a 'swap' variant for replay fixtures
  // captured before the cleanup ran; we render those as a Phase-A skip so
  // historical replays remain readable without a locale-key dependency.
  if (phaseA.kind === 'swap') {
    return intl.formatMessage({ id: 'replay.turn.phase_a.skip' })
  }
  return intl.formatMessage({ id: 'replay.turn.phase_a.skip' })
}

function phaseBSummary(phaseB: PhaseBBody, intl: IntlShape): string {
  if (phaseB.kind === 'move') {
    return intl.formatMessage(
      { id: 'replay.turn.phase_b.move' },
      { from: phaseB.from, to: phaseB.to },
    )
  }
  if (phaseB.kind === 'empower') {
    return intl.formatMessage(
      { id: 'replay.turn.phase_b.empower' },
      { role: phaseB.role },
    )
  }
  if (phaseB.kind === 'powerup') {
    return intl.formatMessage(
      { id: 'replay.turn.phase_b.powerup' },
      { powerup: phaseB.name },
    )
  }
  if (phaseB.kind === 'pass') {
    return intl.formatMessage({ id: 'replay.turn.phase_b.pass' })
  }
  return intl.formatMessage({ id: 'replay.turn.phase_b.extend' })
}

function outcomeKey(status: string, winnerSlot: 0 | 1 | null): string {
  if (
    winnerSlot !== null &&
    (status === 'completed' || status === 'won' || status === 'lost')
  ) {
    return 'replay.outcome.winner'
  }
  return OUTCOME_KEY_FOR_STATUS[status] ?? 'replay.outcome.completed'
}

interface TurnRowProps {
  readonly turn: ReplayTurnRow
  readonly expanded: boolean
  readonly onToggle: (turnIdx: number) => void
  readonly intl: IntlShape
}

function TurnRow({
  turn,
  expanded,
  onToggle,
  intl,
}: TurnRowProps): JSX.Element {
  const detailId = `replay-turn-detail-${turn.turn_idx}`
  const slotLabel = intl.formatMessage(
    { id: 'replay.turn.player_slot' },
    { slot: turn.player_slot === 0 ? 'A' : 'B' },
  )
  const ariaLabel = intl.formatMessage(
    { id: 'replay.turn.row.aria_label' },
    {
      turnNumber: turn.turn_idx,
      playerSlot: turn.player_slot === 0 ? 'A' : 'B',
    },
  )
  const handleClick = useCallback((): void => {
    onToggle(turn.turn_idx)
  }, [onToggle, turn.turn_idx])
  return (
    <li
      data-testid={`replay-turn-row-${turn.turn_idx}`}
      style={{
        listStyle: 'none',
        width: '100%',
      }}
    >
      <button
        type="button"
        className="card"
        onClick={handleClick}
        aria-expanded={expanded ? 'true' : 'false'}
        aria-controls={detailId}
        aria-label={ariaLabel}
        style={{
          // Folk parity: the `.card` recipe owns the borderless near-white
          // surface + warm drop (prototype replay turn rows are <Card>s);
          // override only the slimmer row padding + button affordances.
          width: '100%',
          padding: 'var(--space-3) var(--space-4)',
          color: 'var(--color-text-primary)',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-1)',
          fontFamily: 'var(--font-family-body)',
          fontSize: 'var(--font-size-14)',
        }}
      >
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span
            data-slot
            style={{
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-13)',
              color: 'var(--color-cta-primary)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            #{turn.turn_idx} · {slotLabel}
          </span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-family-body)',
                fontSize: 'var(--font-size-12)',
                color: 'var(--color-text-secondary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <FormattedMessage
                id="replay.turn.bank_label"
                values={{
                  bankA: turn.economy.bank_a,
                  bankB: turn.economy.bank_b,
                }}
              />
            </span>
            {/* Folk parity: accordion chevron (prototype rotates it on
                expand). Decorative — the button already carries aria-expanded
                + aria-label; the rotate transition is globally
                reduced-motion-capped. */}
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                color: 'var(--color-ink-ghost)',
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform var(--duration-fast) var(--ease-out)',
              }}
            >
              <Icon name="chevron" size={16} />
            </span>
          </span>
        </span>
        <span>{phaseASummary(turn.phase_a, intl)}</span>
        <span>{phaseBSummary(turn.phase_b, intl)}</span>
      </button>
      {expanded && (
        <div
          id={detailId}
          data-testid={`replay-turn-detail-${turn.turn_idx}`}
          style={{
            padding: 'var(--space-3) var(--space-4)',
            marginTop: 'var(--space-1)',
            // Folk parity: nested warm-paper sub-card surface.
            backgroundColor: 'var(--color-paper-2)',
            border: 'none',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-13)',
          }}
        >
          <dl style={{ margin: 0, display: 'grid', gap: 'var(--space-1)' }}>
            <dt style={{ fontWeight: 700 }}>
              <FormattedMessage
                id="replay.turn.phase_a"
                defaultMessage="Phase A"
              />
            </dt>
            <dd style={{ margin: 0 }}>{JSON.stringify(turn.phase_a)}</dd>
            <dt style={{ fontWeight: 700 }}>
              <FormattedMessage
                id="replay.turn.phase_b"
                defaultMessage="Phase B"
              />
            </dt>
            <dd style={{ margin: 0 }}>{JSON.stringify(turn.phase_b)}</dd>
            <dt style={{ fontWeight: 700 }}>
              <FormattedMessage
                id="replay.turn.captures"
                defaultMessage="Captures"
              />
            </dt>
            <dd style={{ margin: 0 }}>
              A: {turn.economy.captures_a} · B: {turn.economy.captures_b}
            </dd>
          </dl>
        </div>
      )}
    </li>
  )
}

type PlayerFilter = 'all' | 'a' | 'b'

const PLAYER_FILTER_OPTIONS: ReadonlyArray<SegOption<PlayerFilter>> = [
  { value: 'all', label: 'ALL' },
  { value: 'a', label: 'PLAYER A' },
  { value: 'b', label: 'PLAYER B' },
]

export function ReplayScreen({ id }: ReplayScreenProps): JSX.Element {
  const intl = useIntl()
  const router = useRouter()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  const [expandedTurn, setExpandedTurn] = useState<number | null>(null)
  const [playerFilter, setPlayerFilter] = useState<PlayerFilter>('all')

  // document.title — Phase 18 canonical hook (Plan 18-03).
  useDocumentTitle('doc.title.replay')

  useEffect(() => {
    let cancelled = false
    setState({ kind: 'loading' })
    fetchReplay(id)
      .then((replay) => {
        if (cancelled) return
        setState({ kind: 'loaded', replay })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const errorKind =
          err instanceof ApiError ? mapStatusToError(err.status) : 'generic'
        setState({ kind: 'error', errorKind })
      })
    return () => {
      cancelled = true
    }
  }, [id])

  const handleToggle = useCallback((turnIdx: number): void => {
    setExpandedTurn((current) => (current === turnIdx ? null : turnIdx))
  }, [])

  const handleBackToLobby = useCallback((): void => {
    router.navigate('/lobby')
  }, [router])

  // Sort turns ASC defensively — handler already does this, but be safe.
  const sortedTurns = useMemo(() => {
    if (state.kind !== 'loaded') return [] as ReadonlyArray<ReplayTurnRow>
    return [...state.replay.turns].sort((a, b) => a.turn_idx - b.turn_idx)
  }, [state])

  // Canonical back-header — shared across every load state (back → lobby).
  const replayHeader = (
    <ScreenHeader
      title={<FormattedMessage id="replay.title" />}
      onBack={handleBackToLobby}
      backLabel={intl.formatMessage({ id: 'nav.back', defaultMessage: 'Back' })}
    />
  )

  if (state.kind === 'loading') {
    return (
      <Screen
        name="replay"
        gutter={16}
        contentStyle={{ gap: 'var(--space-3)', alignItems: 'center' }}
        mainProps={{ 'data-replay-id': id, 'data-state': 'loading' }}
        header={replayHeader}
      >
        <p
          role="status"
          aria-live="polite"
          style={{
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <FormattedMessage id="replay.loading" />
        </p>
        <div
          aria-hidden="true"
          style={{
            width: '100%',
            height: '120px',
            // Folk parity: warm-paper skeleton placeholder.
            backgroundColor: 'var(--color-paper-2)',
            border: 'none',
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-card)',
            opacity: 0.6,
          }}
        />
      </Screen>
    )
  }

  if (state.kind === 'error') {
    return (
      <Screen
        name="replay"
        gutter={16}
        contentStyle={{ gap: 'var(--space-4)', alignItems: 'center' }}
        mainProps={{
          'data-replay-id': id,
          'data-state': 'error',
          'data-error-kind': state.errorKind,
        }}
        header={replayHeader}
      >
        <p
          role="alert"
          style={{
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-primary)',
            textAlign: 'center',
            maxWidth: '320px',
          }}
        >
          <FormattedMessage id={ERROR_COPY_KEY[state.errorKind]} />
        </p>
        <Btn
          kind="gold"
          size="md"
          onClick={handleBackToLobby}
          testId="replay-back-to-lobby"
        >
          <FormattedMessage id="replay.back_to_lobby" />
        </Btn>
      </Screen>
    )
  }

  // loaded
  const { replay } = state
  const completedAt = new Date(replay.completed_at)
  const outcomeId = outcomeKey(
    replay.outcome.status,
    replay.outcome.winner_slot,
  )
  // Derive board letters for the read-only Board preview from final_board.
  // The wire shape encodes pieces with a letter glyph inside `piece`; map
  // empty → null per Board's 36-cell row-major contract.
  const finalLetters: ReadonlyArray<string | null> = Array.from(
    { length: 36 },
    (_, idx) => {
      const cell = replay.final_board.find((c) => c.cell_idx === idx)
      return cell?.piece ?? null
    },
  )
  const filteredTurns =
    playerFilter === 'all'
      ? sortedTurns
      : sortedTurns.filter((t) =>
          playerFilter === 'a' ? t.player_slot === 0 : t.player_slot === 1,
        )
  return (
    <Screen
      name="replay"
      gutter={16}
      contentStyle={{ gap: 'var(--space-4)', alignItems: 'center' }}
      mainProps={{ 'data-replay-id': id, 'data-state': 'loaded' }}
      header={replayHeader}
    >
      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-family-body)',
          fontWeight: 500,
          fontSize: 'var(--font-size-13)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <FormattedMessage
          id="replay.completed_at"
          values={{ when: completedAt.toLocaleString() }}
        />
      </p>
      <section
        data-section="replay-final-board"
        aria-label={intl.formatMessage({
          id: 'replay.final_board.aria',
          defaultMessage: 'Final board (read-only)',
        })}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Board view="letters" letters={finalLetters} size={240} />
      </section>
      <Seg<PlayerFilter>
        options={PLAYER_FILTER_OPTIONS}
        value={playerFilter}
        onChange={setPlayerFilter}
        ariaLabel={intl.formatMessage({ id: 'replay.turn_scrubber' })}
        testId="replay-player-filter"
      />
      <section
        data-section="replay-outcome"
        aria-label={intl.formatMessage({ id: outcomeId })}
        style={{
          width: '100%',
          padding: 'var(--space-3) var(--space-4)',
          // Folk parity: the result reads as a warm gold-tinted pill banner
          // (prototype's gold "Victory" result chip), not a boxed slab.
          backgroundColor: 'var(--color-paper-2)',
          border: 'none',
          borderRadius: 'var(--radius-pill)',
          boxShadow: 'var(--shadow-card)',
          color: 'var(--color-accent-gold-deep)',
          textAlign: 'center',
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-14)',
        }}
      >
        <FormattedMessage
          id={outcomeId}
          values={{
            slot:
              replay.outcome.winner_slot === 0
                ? 'A'
                : replay.outcome.winner_slot === 1
                  ? 'B'
                  : '',
          }}
        />
      </section>
      <ol
        data-testid="replay-turn-list"
        style={{
          width: '100%',
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        {filteredTurns.map((turn) => (
          <TurnRow
            key={turn.turn_idx}
            turn={turn}
            expanded={expandedTurn === turn.turn_idx}
            onToggle={handleToggle}
            intl={intl}
          />
        ))}
      </ol>
      <Btn
        kind="gold"
        size="md"
        onClick={handleBackToLobby}
        style={{ marginTop: 'var(--space-3)' }}
        testId="replay-back-to-lobby-loaded"
      >
        <FormattedMessage id="replay.back_to_lobby" />
      </Btn>
    </Screen>
  )
}
